
import type { ConfigDTO, ConfigService, RelatedPrompts } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { RegularFilterSelectedValues } from '../../models'

import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import FiltersAside from '../../components/filters/filters-aside.component.vue'

import { UiProgressLinear } from '../../components/keen-ui'
import RelatedPromptsMetrics from '../../components/related-prompts/related-prompts-metrics.component.vue'
import RelatedPromptsPill from '../../components/related-prompts/related-prompts-pill.component.vue'

import RelatedPromptsPreviewItem from '../../components/related-prompts/related-prompts-preview-item.component.vue'
import SearchBox from '../../components/shared/search-box.component.vue'
import { useInstance, useRelatedPrompts } from '../../composables'

import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'

export default defineComponent({
  name: 'RelatedPromptsPreview',
  components: {
    SearchBox,
    FiltersAside,
    RelatedPromptsMetrics,
    RelatedPromptsPill,
    RelatedPromptsPreviewItem,
    UiProgressLinear,
  },
  setup(_, ctx: SetupContext) {
    const baseClass = 'related-prompts-preview'

    const {
      context,
      messages,
      loading,
      filters,
      isSelectedMandatoryFilters,
      setQuery,
      setFiltersValues,
      loadRelatedPrompts,
      updateRelatedPrompt,
    } = useRelatedPrompts(ctx)
    const { instanceConfig } = useInstance()

    const instanceManagementService = CONTAINER.get<ConfigService>(TYPES.ConfigService)

    const serviceConfigs = ref<ConfigDTO>()
    const relatedPrompts = ref<RelatedPrompts[]>([])
    const selectedRelatedPromptsId = ref<RelatedPrompts['uuid']>()
    const relatedPromptsPreviews = ref([])
    const isPreviewDisabled = ref(false)
    const expandedPillId = ref<RelatedPrompts['uuid']>()
    const numberOfPillsEnabled = ref(0)

    const previewValues = computed(() => relatedPromptsPreviews.value.map(item => item.values))

    const contentClasses = computed(() => [
      `${baseClass}__content`,
      { 'scroll--x': !expandedPillId.value },
      { [`${baseClass}__content--expanded`]: expandedPillId.value },
    ])

    const resetScroll = (): void => {
      const element = document.querySelector('.related-prompts-preview__wrapper--related-prompts')
      element?.scrollTo(0, 0)
    }

    const calculateNumberOfPillsEnabled = (): void => {
      const maxResults = (serviceConfigs.value?.data as any)?.maxResults ?? 0
      if (maxResults === 0) {
        numberOfPillsEnabled.value = 0
        return
      }

      const firstMaxResults = relatedPrompts.value?.slice(0, maxResults) ?? []
      const inactiveCount = firstMaxResults.filter(prompt => !prompt.active).length
      numberOfPillsEnabled.value = inactiveCount + maxResults
    }

    const showPill = (item: RelatedPrompts): boolean => {
      return !expandedPillId.value || item.uuid === expandedPillId.value
    }

    const resetExpandedState = (): void => {
      expandedPillId.value = undefined
      relatedPromptsPreviews.value = []
    }

    const loadRelatedPromptsData = async (): Promise<void> => {
      relatedPrompts.value = await loadRelatedPrompts()
      calculateNumberOfPillsEnabled()
    }

    const updateFilters = (selectedValues: Record<string, RegularFilterSelectedValues>): void => {
      setFiltersValues(selectedValues)
    }

    const handleTogglePromptEnabled = async (item: RelatedPrompts): Promise<void> => {
      await updateRelatedPrompt(item)
      resetScroll()
      resetExpandedState()
      await loadRelatedPromptsData()
    }

    const handlePillExpanded = (item: RelatedPrompts, expanded: boolean): void => {
      expandedPillId.value = expanded ? item.uuid : undefined

      if (expanded) {
        resetScroll()
      }
    }

    const handleContextOrFiltersChange = async (): Promise<void> => {
      resetScroll()
      resetExpandedState()
      await loadRelatedPromptsData()
    }

    watch([context, filters], handleContextOrFiltersChange, { deep: true })

    const initializeComponent = async (): Promise<void> => {
      await loadRelatedPromptsData()

      serviceConfigs.value = await instanceManagementService.read({
        instance: instanceConfig.value.id,
        service: 'relatedintents',
      })

      calculateNumberOfPillsEnabled()
    }

    onMounted(initializeComponent)

    return {
      baseClass,
      context,
      messages,
      loading,
      filters,
      isSelectedMandatoryFilters,
      relatedPrompts,
      selectedRelatedPromptsId,
      relatedPromptsPreviews,
      isPreviewDisabled,
      expandedPillId,
      numberOfPillsEnabled,
      previewValues,
      contentClasses,
      updateFilters,
      setQuery,
      handleTogglePromptEnabled,
      handlePillExpanded,
      showPill,
    }
  },
})
